import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download, Plus, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { EquipmentFormModal } from '../../components/EquipmentFormModal';
import { ConditionBadge, EquipmentStatusBadge } from '../../components/facilities-ui';
import { Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Td, Th, cn } from '../../components/ui';
import { api, apiUrl } from '../../lib/api';
import { useDesk } from '../../lib/desk';
import {
  CONDITION_LABEL,
  describeRemainingLife,
  FACILITY_SYSTEM_LABEL,
  placeLabel,
  useFacilityPlaces,
  type EquipmentListItem,
  type FacilityCondition,
  type FacilitySystem,
} from '../../lib/facilities';
import { formatUtcDate, toDateInput } from '../../lib/format';
import { useDebouncedValue, useUrlState } from '../../lib/hooks';
import type { Page } from '../../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = { search: '', buildingId: '', system: '', condition: '', retired: '', page: '1' };

function LifecycleCell({ item }: { item: EquipmentListItem }) {
  const parts: string[] = [];
  if (item.installedOn) parts.push(`Installed ${item.installedOn.slice(0, 4)}`);
  if (item.replacementYear !== null) parts.push(`${parts.length ? 'replace' : 'Replace'} ${item.replacementYear}`);
  const remaining = describeRemainingLife(item.remainingYears);
  if (!parts.length && !remaining) return <span className="text-slate-400">—</span>;
  return (
    <>
      {parts.length > 0 && <p className="whitespace-nowrap">{parts.join(' · ')}</p>}
      {remaining && (
        <p className={cn('text-xs whitespace-nowrap', (item.remainingYears ?? 0) < 0 ? 'font-medium text-red-700' : 'text-slate-500')}>{remaining}</p>
      )}
    </>
  );
}

export function EquipmentPage() {
  const desk = useDesk();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const places = useFacilityPlaces();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const query = useQuery({
    queryKey: ['facilities', 'equipment', 'list', filters],
    queryFn: () =>
      api.get<Page<EquipmentListItem>>('/facilities/assets', {
        search: filters.search,
        buildingId: filters.buildingId,
        system: filters.system,
        condition: filters.condition,
        includeRetired: filters.retired === '1' || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const today = toDateInput(new Date());
  const canAdd = desk.can('TECHNICIAN');
  const hasFilters = !!(filters.search || filters.buildingId || filters.system || filters.condition || filters.retired);

  return (
    <>
      <PageHeader
        title="Equipment"
        description="Building systems and equipment the facilities team maintains, with their history and lifecycle"
        actions={
          <>
            <a href={apiUrl('/facilities/reports/export/equipment.csv')}>
              <Button icon={<Download />}>Export CSV</Button>
            </a>
            {canAdd && (
              <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                Add equipment
              </Button>
            )}
          </>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name, code, serial, make, model, or room"
            className="w-full sm:w-80"
          />
          <Select value={filters.buildingId} onChange={(event) => setFilters({ buildingId: event.target.value })} className="w-auto">
            <option value="">All buildings</option>
            {places.data?.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </Select>
          <Select value={filters.system} onChange={(event) => setFilters({ system: event.target.value })} className="w-auto">
            <option value="">All systems</option>
            {(Object.keys(FACILITY_SYSTEM_LABEL) as FacilitySystem[]).map((value) => (
              <option key={value} value={value}>
                {FACILITY_SYSTEM_LABEL[value]}
              </option>
            ))}
          </Select>
          <Select value={filters.condition} onChange={(event) => setFilters({ condition: event.target.value })} className="w-auto">
            <option value="">Any condition</option>
            {(Object.keys(CONDITION_LABEL) as FacilityCondition[]).map((value) => (
              <option key={value} value={value}>
                {CONDITION_LABEL[value]}
              </option>
            ))}
          </Select>
          <label className="inline-flex items-center gap-2 px-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={filters.retired === '1'}
              onChange={(event) => setFilters({ retired: event.target.checked ? '1' : '' })}
              className="size-4 rounded border-slate-300 text-brand-600"
            />
            Include retired
          </label>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setFilters({ search: '', buildingId: '', system: '', condition: '', retired: '' });
              }}
            >
              Clear filters
            </Button>
          )}
          {query.isFetching && !query.isLoading && <Spinner className="size-4" />}
        </div>

        {query.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : query.error || !query.data ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyState
            icon={<Wrench />}
            title={hasFilters ? 'No equipment matches these filters' : 'No equipment yet'}
            description={
              hasFilters
                ? 'Try a different search or clear the filters.'
                : 'Add boilers, rooftop units, fire panels, playgrounds, and other equipment to track their maintenance, costs, and when they’ll need replacing.'
            }
            action={
              !hasFilters &&
              canAdd && (
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  Add equipment
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Equipment</Th>
                  <Th className="hidden md:table-cell">System</Th>
                  <Th className="hidden lg:table-cell">Where</Th>
                  <Th>Condition</Th>
                  <Th className="hidden sm:table-cell">Status</Th>
                  <Th className="hidden md:table-cell">Lifecycle</Th>
                  <Th className="hidden text-right sm:table-cell">Open work</Th>
                  <Th className="hidden lg:table-cell">Next maintenance</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {query.data.items.map((item) => (
                  <tr key={item.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/facilities/equipment/${item.id}`)}>
                    <Td className="max-w-xs">
                      <Link
                        to={`/facilities/equipment/${item.id}`}
                        onClick={(event) => event.stopPropagation()}
                        className="block truncate font-medium text-slate-900 hover:text-brand-700"
                      >
                        {item.name}
                      </Link>
                      {item.code && <p className="truncate font-mono text-xs text-slate-500">{item.code}</p>}
                    </Td>
                    <Td className="hidden whitespace-nowrap md:table-cell">{FACILITY_SYSTEM_LABEL[item.system]}</Td>
                    <Td className="hidden max-w-[14rem] lg:table-cell">
                      <p className="truncate">{placeLabel(item) || <span className="text-slate-400">—</span>}</p>
                    </Td>
                    <Td>
                      <ConditionBadge condition={item.condition} />
                    </Td>
                    <Td className="hidden sm:table-cell">
                      <EquipmentStatusBadge status={item.status} />
                    </Td>
                    <Td className="hidden md:table-cell">
                      <LifecycleCell item={item} />
                    </Td>
                    <Td className="hidden text-right sm:table-cell">
                      {item.openWorkOrders > 0 ? (
                        <span className="font-medium text-slate-900">{item.openWorkOrders.toLocaleString()}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </Td>
                    <Td className="hidden whitespace-nowrap lg:table-cell">
                      {item.nextMaintenanceDue ? (
                        <span className={cn(item.nextMaintenanceDue < today && 'font-medium text-red-700')}>{formatUtcDate(item.nextMaintenanceDue)}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={query.data.total} onPage={(next) => setFilters({ page: String(next) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <EquipmentFormModal open={creating} onClose={() => setCreating(false)} onSaved={(detail) => navigate(`/facilities/equipment/${detail.id}`)} />
    </>
  );
}
